import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Users, Palette } from 'lucide-react';

export default function DanceFloorsTwo() {
  const navigate = useNavigate();
  const [colorScheme, setColorScheme] = useState('standard');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">NVC Dance Floors (Two People)</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setColorScheme(colorScheme === 'standard' ? 'steve' : 'standard')}
                variant="outline"
                size="sm"
                className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20"
              >
                <Palette className="h-4 w-4 mr-2" />
                {colorScheme === 'standard' ? "Steve's Colors" : 'Standard Colors'}
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 12</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-8">
            <Users className="h-16 w-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-3">Interactive Communication Between Two People</h2>
            <p className="text-lg text-slate-300 leading-relaxed max-w-4xl mx-auto">
              This diagram shows how two people can dance together through NVC, each moving between 
              self-empathy, honesty, and empathy as they communicate.
            </p>
          </div>

          {/* Key Insights */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-cyan-200 mb-4">Person A's Dance Floors</h3>
              <p className="text-slate-200 leading-relaxed mb-3">
                Person A has their own three spaces: self-empathy, honesty (expressing), and empathy (listening to B).
              </p>
              <p className="text-slate-300 text-sm italic">
                They can choose to connect with themselves, express what's alive in them, or empathize with Person B.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-200 mb-4">Person B's Dance Floors</h3>
              <p className="text-slate-200 leading-relaxed mb-3">
                Person B also has their own three spaces: self-empathy, honesty (expressing), and empathy (listening to A).
              </p>
              <p className="text-slate-300 text-sm italic">
                The dance happens as both people move fluidly between their spaces in response to each other.
              </p>
            </div>
          </div>

          {/* The Dance in Action */}
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">The Dance in Action</h3>
            <div className="space-y-4 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold text-green-200">Step 1:</span> Person A might start in self-empathy, 
                connecting with their own feelings and needs about the situation.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-green-200">Step 2:</span> When ready, Person A moves to honesty, 
                expressing their observations, feelings, needs, and requests to Person B.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-green-200">Step 3:</span> Person B might respond from empathy, 
                reflecting back what they heard and guessing Person A's feelings and needs.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-green-200">Step 4:</span> Or Person B might go to self-empathy first, 
                processing their own reaction before responding.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-green-200">Step 5:</span> The dance continues as both people 
                move between their three spaces, creating connection and understanding.
              </p>
            </div>
          </div>

          {/* Teaching Notes */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Notes</h3>
            <div className="space-y-3 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold">Key Concept:</span> Each person has their own complete set of dance floors. 
                There's no "right" sequence—the dance emerges from staying connected to feelings and needs.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Common Pattern:</span> Often one person expresses (honesty) while the other 
                listens (empathy), then they switch roles. But it's flexible—sometimes both need self-empathy first.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Practice Tip:</span> Have students practice in pairs with a simple scenario. 
                Encourage them to name which "dance floor" they're on as they move through the conversation.
              </p>
              <p className="leading-relaxed mt-4 text-slate-300 italic">
                The visual diagram shows all the possibilities. The beauty is in the fluidity—following what's needed 
                in each moment rather than following a rigid script.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
